/*
  ODC strict search
  -----------------
  Shopify's native storefront search treats a multi-word query as "match ANY of these
  words", so "pantaloncino argentina" returns every pair of shorts AND every Argentina
  item - 297 results, with Brasile / Inter / Napoli shorts on page 1.

  Unscoped boolean operators are ignored by Shopify (`pantaloncino AND argentina` still
  returns 231 results), but FIELD-SCOPED terms are honoured. So on submit we rewrite

      pantaloncino argentina
  ->  (title:pantaloncino* OR tag:pantaloncino*) AND (title:argentina* OR tag:argentina*)

  which means "every word must appear in the title or in a tag" = 2 results.

  Notes
  - The tag half of each group is what rescues singular/plural: products titled
    "PANTALONCINO ..." also carry the tag "pantaloncini", so both spellings still match.
  - If the shopper already typed advanced syntax (title:, tag:, quotes) we leave the
    query completely alone.
  - If the strict query finds nothing, the results page falls back to the plain query
    (see odcSearchFallback below), so we can never show an empty page that Shopify
    would have filled.
  - Display of the query is cleaned up in Liquid (snippets/odc-search-terms.liquid),
    so the shopper never reads the operators back.
*/
(function () {
  'use strict';

  // Words that add no signal and would wrongly narrow an AND query.
  var STOPWORDS = [
    'di', 'da', 'de', 'del', 'dello', 'della', 'dei', 'degli', 'delle', 'd',
    'e', 'ed', 'il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'uno', 'una',
    'per', 'con', 'a', 'al', 'ai', 'alla', 'in', 'su', 'da',
    'the', 'of', 'and', 'or', 'y', 'el'
  ];

  // Characters Shopify parses as search operators - strip them from shopper input.
  var OPERATOR_CHARS = /[()"'*:\\~^{}\[\]]/g;

  var MARKER = 'title:';

  function alreadyAdvanced(q) {
    return /(?:^|[\s(])(?:title|tag|product_type|vendor|body|variants\.sku|sku|id|handle)\s*:/i.test(q) ||
      q.indexOf('"') !== -1;
  }

  // Tokens this short are not selective enough to AND on, and Shopify's index ignores
  // some of them outright: "as roma" searched as (title:as* AND title:roma*) returns
  // NOTHING even though products are titled "AS Roma maglia away 2023/24". Dropping them
  // leaves "roma", which is what the shopper meant anyway.
  var MIN_TERM_LENGTH = 3;

  function collectTerms(words, dropStopwords) {
    var groups = [];
    var used = {};

    for (var i = 0; i < words.length; i++) {
      var w = words[i].replace(OPERATOR_CHARS, '').trim();
      if (!w) continue;
      if (w.length < MIN_TERM_LENGTH) continue;
      var key = w.toLowerCase();
      if (dropStopwords && STOPWORDS.indexOf(key) !== -1) continue;
      if (used[key]) continue;                             // don't AND the same word twice
      used[key] = true;
      groups.push('(title:' + w + '* OR tag:' + w + '*)');
    }

    return groups;
  }

  function buildStrictQuery(raw) {
    var words = String(raw).replace(/[‘’“”]/g, ' ').split(/[\s,;+\/]+/);

    var groups = collectTerms(words, true);

    // If stripping stopwords left nothing to AND, the "stopword" was part of a name -
    // "del piero" is a player, not grammar. Keep them and try again.
    if (groups.length < 2) {
      groups = collectTerms(words, false);
    }

    // Single-word searches are not the problem - "juventus" or "argentina" already
    // return what the shopper wants, and 71% of this store's search volume is a single
    // word. Only multi-word queries get rewritten, so the common case is untouched.
    if (groups.length < 2) return null;
    return groups.join(' AND ');
  }

  function searchUrlFor(form, strictQuery) {
    var action = form.getAttribute('action') || '/search';
    var url;
    try {
      url = new URL(action, window.location.origin);
    } catch (e) {
      url = new URL('/search', window.location.origin);
    }
    url.search = '';
    url.searchParams.set('q', strictQuery);
    url.searchParams.set('options[prefix]', 'last');

    // Carry over the sort the shopper had chosen, if any.
    var sort = form.querySelector('[name="sort_by"]');
    if (sort && sort.value) url.searchParams.set('sort_by', sort.value);

    return url.pathname + '?' + url.searchParams.toString();
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/search') === -1) return;

    // The facet / sort form on the results page already carries a rewritten q in a
    // hidden input - never touch it.
    var input = form.querySelector('input[type="search"][name="q"]');
    if (!input) return;

    var raw = (input.value || '').trim();
    if (!raw) return;
    if (alreadyAdvanced(raw)) return;

    var strict = buildStrictQuery(raw);
    if (!strict) return;

    event.preventDefault();
    window.location.assign(searchUrlFor(form, strict));
  }

  document.addEventListener('submit', onSubmit, true);

  // Shopify's predictive dropdown offers curated query completions as plain links.
  // Rewrite those too, so clicking a suggestion behaves like typing it.
  document.addEventListener(
    'click',
    function (event) {
      var a = event.target && event.target.closest ? event.target.closest('a[href*="q="]') : null;
      if (!a) return;
      if (!a.closest('.predictive-search')) return;

      var href;
      try {
        href = new URL(a.getAttribute('href'), window.location.origin);
      } catch (e) {
        return;
      }
      if (href.pathname.indexOf('/search') === -1) return;

      var q = href.searchParams.get('q');
      if (!q || alreadyAdvanced(q)) return;

      var strict = buildStrictQuery(q);
      if (!strict) return;

      href.searchParams.set('q', strict);
      href.searchParams.set('options[prefix]', 'last');
      a.setAttribute('href', href.pathname + '?' + href.searchParams.toString());
    },
    true
  );

  /*
    Safety net: if a strict query returned nothing, re-run the shopper's plain query
    instead of showing an empty results page. Runs once - the retry URL has no
    operators, so it cannot loop.
  */
  function odcSearchFallback() {
    var params = new URLSearchParams(window.location.search);
    var q = params.get('q');
    if (!q || q.indexOf(MARKER) === -1) return;

    var empty = document.querySelector('.template-search--empty');
    if (!empty) return;

    // Any active filter is a legitimate reason for 0 results - leave it alone.
    var hasFilters = false;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) hasFilters = true;
    });
    if (hasFilters) return;

    var plain = q
      .replace(/\(|\)/g, ' ')
      .replace(/\bAND\b|\bOR\b/g, ' ')
      .replace(/\b(?:title|tag):/g, ' ')
      .replace(/\*/g, ' ')
      .split(/\s+/)
      .filter(function (w, i, arr) { return w && arr.indexOf(w) === i; })
      .join(' ');

    if (!plain) return;

    var retry = new URLSearchParams();
    retry.set('q', plain);
    retry.set('options[prefix]', 'last');
    window.location.replace(window.location.pathname + '?' + retry.toString());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', odcSearchFallback);
  } else {
    odcSearchFallback();
  }
})();
